import { v4 } from "uuid";

import { OnsiteEditDto } from "@intelligems/ig-types/dtos/onsite-edits";
import { assignOnsiteEditGroupIds } from "@intelligems/ig-utils/onsite-edit";
import * as rtk from "@reduxjs/toolkit";
import { createSelector, type PayloadAction } from "@reduxjs/toolkit";

import { IOnsiteEditsState } from "./types";

const sliceInitialOnsiteEditsState: IOnsiteEditsState = {
  isSuccess: false,
  allEdits: [],
  storedEdits: [],
  hasNewVisualBuilderMod: false,
};

const slide = rtk.createSlice({
  name: "onsiteEdits",
  initialState: sliceInitialOnsiteEditsState,
  reducers: {
    resetOnsiteEdits: () => sliceInitialOnsiteEditsState,
    setOnsiteEdits: (draft, action: PayloadAction<OnsiteEditDto[]>) => {
      draft.allEdits = action.payload;
    },
    modClearOnsiteEdits: (draft) => {
      draft.allEdits = [];
    },
    setOnsiteEdit: (draft, action: PayloadAction<OnsiteEditDto>) => {
      const index = draft.allEdits.findIndex((e) => e.id === action.payload.id);
      if (index > -1) {
        draft.allEdits[index] = action.payload;
      } else {
        draft.allEdits.push(action.payload);
      }
    },
    deleteOnsiteEdit: (draft, action: PayloadAction<string>) => {
      draft.allEdits = draft.allEdits.filter(
        (edit) => edit.id !== action.payload
      );
    },
    setOnsiteEditsByVariationId: (
      draft,
      action: PayloadAction<{
        onsiteEdits: OnsiteEditDto[];
        variationId: string;
      }>
    ) => {
      const updatedDraft = draft.allEdits.filter(
        (edit) => edit.variationId !== action.payload.variationId
      );

      const dupEdits: OnsiteEditDto[] = action.payload.onsiteEdits.map(
        (edit) => {
          return {
            ...edit,
            variationId: action.payload.variationId,
            id: v4(),
          };
        }
      );

      const formatted = [...updatedDraft, ...dupEdits].map((onsiteEdit) => {
        return { ...onsiteEdit };
      });

      assignOnsiteEditGroupIds(formatted, v4);

      draft.allEdits = formatted;
    },
    resetOnsiteEditsByVariationId: (
      draft,
      action: PayloadAction<{ variationId: string }>
    ) => {
      draft.allEdits = draft.allEdits.filter(
        (edit) => edit.variationId !== action.payload.variationId
      );
    },
    initOnsiteEdits: (draft, action: PayloadAction<OnsiteEditDto[]>) => {
      draft.isSuccess = true;
      const formatted = action.payload.map((onsiteEdit) => {
        return { ...onsiteEdit };
      });
      assignOnsiteEditGroupIds(formatted, v4);
      draft.allEdits = formatted;
      draft.storedEdits = formatted;
      draft.hasNewVisualBuilderMod = false;
    },
    markNewVisualBuilderMod: (draft) => {
      draft.hasNewVisualBuilderMod = true;
    },
  },
  selectors: {
    selectedOnsiteEdits: (state) => state.allEdits,
    selectedOnsiteEditsState: (state) => state,
  },
});

export const {
  initOnsiteEdits,
  setOnsiteEdits,
  resetOnsiteEdits,
  setOnsiteEdit,
  deleteOnsiteEdit,
  modClearOnsiteEdits,
  markNewVisualBuilderMod,
  setOnsiteEditsByVariationId,
  resetOnsiteEditsByVariationId,
} = slide.actions;

export const onsiteEditsReducer = slide.reducer;

export const { selectedOnsiteEdits, selectedOnsiteEditsState } =
  slide.selectors;

export const getVariationSpecificOnsiteEdits = (variationId: string) =>
  createSelector(selectedOnsiteEdits, (allEdits) => {
    // Filter the onsite edits based on the provided variationId
    return allEdits.filter((edit) => edit.variationId === variationId);
  });
